import { useContext, useMemo, useState } from "react";
import { Modal } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
    updateDaypartingDataObject,
    updateDayPartingDisplay,
    updateIsAllCampaignsSelected,
    updateIsDaypartingEdit,
} from "@features/modalSlice";
import TimezonePicker from "@drop-downs/TimezonePicker";
import DayPartingBody from "./DayPartingBody";
import DayPartingSelectionCount from "./DayPartingSelectionCount";
import { dprToggleChangeHandler, onSubmitClickHandler } from "@ts/day-parting";
import { mainContext } from "@ts/constants";
import { DPR_TIME_SLOTS, initialDaypartingDataObject } from "@ts/constants";
import ToggleSwitch from "@buttons/ToggleSwitch";
import { updateAllSysCampaignId } from "@features/selectAllGridDataSlice";
import { onSelectAllGridDataClick } from "@ts/utils";
import {
    IMainContext,
    TDayPartingDisplay,
    TIsDaypartingEdit,
} from "@ts/common-types";
import { RowNode } from "ag-grid-community";
import { AiOutlineClose } from "react-icons/ai";
import usePermissionCheck from "@/hooks/usePermissionCheck";

const DayPartingModal = () => {
    const {
        activeCheckedNode,
        dprTimeSlots,
        setDprTimeSlots,
        selectedValue,
        setSelectedValue,
        setDaypartingCardCheckedData,
        campaignsGridApi,
    } = useContext<IMainContext>(mainContext);
    const [fullscreen] = useState<true | string>(true);
    const dayPartingDisplay = useSelector(
        (state: TDayPartingDisplay) => state.dayPartingDisplay.value
    );
    const isDaypartingEdit = useSelector(
        (state: TIsDaypartingEdit) => state.isDaypartingEdit.value
    );

    const selectedCampaignsData = useMemo(() => {
        if (dayPartingDisplay) {
            return activeCheckedNode
                ? activeCheckedNode.map((node: RowNode) =>
                      node.data.sys_campaign_id.toString()
                  )
                : [];
        }
    }, [activeCheckedNode, dayPartingDisplay]);
    const dispatch = useDispatch();
    const handleClose = () => {
        dispatch(updateDayPartingDisplay(false));
        setSelectedValue("");
        setDprTimeSlots(DPR_TIME_SLOTS());
        dispatch(updateDaypartingDataObject(initialDaypartingDataObject));
        dispatch(updateIsDaypartingEdit(false));
        dispatch(updateIsAllCampaignsSelected(false));
        dispatch(updateAllSysCampaignId([]));
        setDaypartingCardCheckedData({
            monday: {},
            tuesday: {},
            wednesday: {},
            thursday: {},
            friday: {},
            saturday: {},
            sunday: {},
        });
    };

    const { canManageDayParting } = usePermissionCheck();

    const HeaderComponent = useMemo(() => {
        return (
            <div
                style={{
                    width: "100%",
                    display: "flex",
                    fontSize: "0.9rem",
                    justifyContent: "space-between",
                }}
                className="d-flex align-items-center pb-3"
            >
                <div className="d-flex">
                    <div>
                        <label
                            style={{
                                fontSize: "1.5rem",
                                fontWeight: "600",
                                marginRight: "3rem",
                            }}
                        >
                            {isDaypartingEdit ? "Edit" : "Set"} Ad Scheduling
                        </label>
                        {isDaypartingEdit ? (
                            <div
                                className="d-flex align-items-center"
                                style={{ marginTop: "3px" }}
                            >
                                <label
                                    className="text-secondary"
                                    style={{
                                        fontSize: "0.8rem",
                                        fontWeight: "700",
                                    }}
                                >
                                    Disable/Enable All &nbsp;
                                </label>
                                <div style={{ marginTop: "4px" }}>
                                    <ToggleSwitch
                                        id="toggle-all-dpr"
                                        onToggleChange={() =>
                                            dprToggleChangeHandler(
                                                dprTimeSlots,
                                                setDprTimeSlots
                                            )
                                        }
                                        size="0.7rem"
                                        disabled={!canManageDayParting}
                                    />
                                </div>
                            </div>
                        ) : null}
                    </div>
                    <DayPartingSelectionCount />
                </div>

                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    {dayPartingDisplay &&
                    !isDaypartingEdit &&
                    !selectedCampaignsData.length ? (
                        <button
                            id="select-all-campaigns-btn"
                            className="btn btn-primary"
                            style={{
                                fontSize: "0.8rem",
                                height: "2rem",
                            }}
                            onClick={() =>
                                onSelectAllGridDataClick(campaignsGridApi)
                            }
                        >
                            Select All Campaigns
                        </button>
                    ) : null}

                    <div style={{ marginRight: "40px" }}>
                        <TimezonePicker
                            fetchData={dayPartingDisplay}
                            style={{
                                width: "18rem",
                                fontSize: "0.75rem",
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    }, [
        dayPartingDisplay,
        isDaypartingEdit,
        selectedCampaignsData,
        dprTimeSlots,
        setDprTimeSlots,
        canManageDayParting,
    ]);

    return (
        <>
            <Modal
                title={HeaderComponent}
                open={dayPartingDisplay}
                onCancel={handleClose}
                closeIcon={<AiOutlineClose style={{ fontSize: "67px" }} />}
                footer={[
                    <button
                        className="btn btn-secondary me-2"
                        type="button"
                        data-bs-dismiss="modal"
                        onClick={handleClose}
                    >
                        Close
                    </button>,
                    <button
                        id="day-parting-submit-btn"
                        className="btn btn-primary"
                        type="button"
                        data-bs-dismiss="modal"
                        disabled={!canManageDayParting}
                        onClick={() =>
                            onSubmitClickHandler(
                                selectedCampaignsData,
                                selectedValue,
                                dprTimeSlots,
                                handleClose
                            )
                        }
                    >
                        Submit
                    </button>,
                ]}
                style={{ top: 20 }}
                width="95vw"
                styles={{
                    body: {
                        height: "66vh",
                        overflowY: "auto",
                    },
                }}
            >
                <DayPartingBody canManageDayParting={canManageDayParting} />
            </Modal>
        </>
    );
};

export default DayPartingModal;
